import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, siteApi, requirementApi, transferApi } from '../api';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/common/StatusBadge';
import ItemLookupPanel from '../components/common/ItemLookupPanel';
import SearchableSelect from '../components/common/SearchableSelect';

// ── Value formatters ──────────────────────────────────────────────────────────
/** Compact: ₹1.2L, ₹45K, ₹850 */
const fmtV = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000)   return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000)     return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};
/** Full Indian format: ₹1,23,456 */
const fmtFull = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/** Trend % between two values (null if no baseline) */
const trendPct = (curr, prev) =>
  prev && prev > 0 ? ((curr - prev) / prev) * 100 : null;

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, trend, icon, bg, textColor, to, onClick }) => {
  const pct       = trend != null ? Number(trend) : null;
  const clickable = !!(to || onClick);
  const inner = (
    <div className={`group relative overflow-hidden rounded-xl border bg-white shadow-sm transition-all duration-200 p-4 h-full
      ${clickable
        ? 'border-gray-200 hover:shadow-md hover:border-gray-300 cursor-pointer'
        : 'border-gray-200'}`}>
      {/* Subtle bg tint */}
      <div className={`absolute inset-0 opacity-[0.04] ${bg}`} />
      {/* Clickable hint — fades in on hover */}
      {clickable && (
        <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150
          text-[9px] font-bold text-slate-400 bg-slate-100 rounded px-1 py-0.5 leading-none select-none">
          details
        </span>
      )}
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg} ${textColor} flex-shrink-0`}>
            {icon}
          </div>
          {pct != null && (
            <span className={`flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              pct >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
            }`}>
              {pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
            </span>
          )}
        </div>
        <p className={`text-xl font-extrabold ${textColor} leading-tight`}>{value}</p>
        <p className="text-xs font-semibold text-slate-600 mt-0.5 leading-snug">{label}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
  if (to)      return <Link to={to} className="block h-full">{inner}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="block h-full w-full text-left">{inner}</button>;
  return <div className="h-full">{inner}</div>;
};

// ── Stacked Bar Chart (pixel-accurate, no external lib) ───────────────────────
const CHART_H = 148; // px

function StackedBarChart({ data }) {
  const maxV = Math.max(...data.map(d => d.matValue + d.statValue), 1);
  return (
    <div className="flex items-end gap-1" style={{ height: CHART_H + 22 }}>
      {data.map((d, i) => {
        const total  = d.matValue + d.statValue;
        const barPx  = Math.max(Math.round((total / maxV) * CHART_H), total > 0 ? 3 : 0);
        const matPx  = total > 0 ? Math.floor((d.matValue / total) * barPx)  : 0;
        const statPx = barPx - matPx;
        return (
          <div key={i} className="flex-1 flex flex-col items-center group relative">
            {/* spacer so bar pins to bottom */}
            <div style={{ height: CHART_H }} className="w-full flex flex-col justify-end">
              {barPx > 0 && (
                <div style={{ height: barPx }} className="w-full rounded-t-sm overflow-hidden flex flex-col">
                  {statPx > 0 && <div style={{ height: statPx }} className="bg-violet-400 w-full flex-shrink-0" />}
                  {matPx  > 0 && <div style={{ height: matPx }}  className="bg-indigo-500 w-full flex-shrink-0" />}
                </div>
              )}
            </div>
            <span className="text-[8px] text-gray-400 mt-1.5 leading-none select-none">{d.label}</span>
            {/* Tooltip */}
            <div className="pointer-events-none hidden group-hover:flex absolute bottom-full mb-5 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] rounded-lg px-2.5 py-2 whitespace-nowrap z-30 shadow-xl flex-col gap-0.5">
              <p className="text-[11px] font-bold border-b border-white/20 pb-0.5 mb-0.5">{d.label}</p>
              {d.matValue  > 0 && <p><span className="inline-block w-2 h-2 bg-indigo-400 rounded-sm mr-1" />Mat: {fmtV(d.matValue)}</p>}
              {d.statValue > 0 && <p><span className="inline-block w-2 h-2 bg-violet-300 rounded-sm mr-1" />Stat: {fmtV(d.statValue)}</p>}
              <p className="font-semibold border-t border-white/20 mt-0.5 pt-0.5">Total: {fmtV(total)}</p>
              {d.deductionValue > 0 && <p className="text-red-300">Ded: {fmtV(d.deductionValue)}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Mini deduction trend bars ─────────────────────────────────────────────────
function DeductionSparkline({ data }) {
  const max = Math.max(...data.map(d => d.total || 0), 1);
  return (
    <div className="flex items-end gap-0.5 h-5">
      {data.map((d, i) => (
        <div
          key={i}
          className="flex-1 bg-red-300 rounded-sm"
          style={{ height: d.total > 0 ? `${Math.max((d.total / max) * 100, 5)}%` : '2px', opacity: d.total > 0 ? 1 : 0.2 }}
          title={`${d.label}: ${fmtV(d.total)}`}
        />
      ))}
    </div>
  );
}

// ── Horizontal value bar ──────────────────────────────────────────────────────
const HValBar = ({ label, value, max, color = 'bg-indigo-500', sub }) => (
  <div>
    <div className="flex items-center justify-between mb-0.5">
      <span className="text-xs text-gray-700 truncate max-w-[170px]" title={label}>{label}</span>
      <span className="text-xs font-bold text-gray-800 ml-2 flex-shrink-0">{fmtV(value)}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-2 ${color} rounded-full`} style={{ width: max > 0 ? `${(value / max) * 100}%` : '0%' }} />
    </div>
    {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ── Inventory breakdown donut (CSS approximation) ─────────────────────────────
function InvBreakdown({ mat, durable, consumable }) {
  const total = mat + durable + consumable;
  // Only the percentage-bar denominator needs a divide-by-zero guard —
  // the displayed "Total Inventory" figure below must show the real total.
  const safeTotal = total || 1;
  const pctMat = (mat / safeTotal) * 100;
  const pctDur = (durable / safeTotal) * 100;
  const pctCon = (consumable / safeTotal) * 100;
  return (
    <div className="space-y-2.5">
      {[
        { label: 'Materials',        value: mat,        pct: pctMat, color: 'bg-indigo-500' },
        { label: 'Durable Assets',   value: durable,    pct: pctDur, color: 'bg-violet-500' },
        { label: 'Consumable Stock', value: consumable, pct: pctCon, color: 'bg-teal-400'   },
      ].map(r => (
        <div key={r.label}>
          <div className="flex justify-between text-xs mb-0.5">
            <span className="text-gray-600 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-sm ${r.color} inline-block`} />
              {r.label}
            </span>
            <span className="font-semibold text-gray-800">{fmtV(r.value)} <span className="text-gray-400 font-normal">({r.pct.toFixed(0)}%)</span></span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-2 ${r.color} rounded-full`} style={{ width: `${r.pct}%` }} />
          </div>
        </div>
      ))}
      <div className="flex justify-between text-xs pt-1 border-t border-gray-100">
        <span className="text-gray-500 font-medium">Total Inventory</span>
        <span className="font-extrabold text-gray-800">{fmtFull(total)}</span>
      </div>
    </div>
  );
}

// ── Materials-by-type breakdown (mirrors InvBreakdown, 4-way instead of 3) ────
// Keys match dashboard.js's inventoryBreakdown.materialsByType response shape.
const MAT_TYPE_ROWS = [
  { key: 'fixedAssets',      label: 'Fixed Assets',      color: 'bg-blue-500' },
  { key: 'powerTools',       label: 'Power Tools',       color: 'bg-orange-500' },
  { key: 'consumableAssets', label: 'Consumable Assets', color: 'bg-teal-400' },
  { key: 'safetyItems',      label: 'Safety Items',      color: 'bg-indigo-500' },
];

function MatTypeBreakdown({ byType }) {
  const rows = MAT_TYPE_ROWS.map((t) => ({
    label: t.label,
    value: byType?.[t.key]?.inventoryValue || 0,
    issued: byType?.[t.key]?.issuedValue || 0,
    color: t.color,
  }));
  const total = rows.reduce((s, r) => s + r.value, 0);
  const safeTotal = total || 1;
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex justify-between text-xs mb-0.5">
            <span className="text-gray-600 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-sm ${r.color} inline-block`} />
              {r.label}
            </span>
            <span className="font-semibold text-gray-800">
              {fmtV(r.value)} <span className="text-gray-400 font-normal">({((r.value / safeTotal) * 100).toFixed(0)}%)</span>
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-2 ${r.color} rounded-full`} style={{ width: `${(r.value / safeTotal) * 100}%` }} />
          </div>
          {r.issued > 0 && <p className="text-[10px] text-gray-400 mt-0.5">{fmtV(r.issued)} currently issued</p>}
        </div>
      ))}
      <div className="flex justify-between text-xs pt-1 border-t border-gray-100">
        <span className="text-gray-500 font-medium">Total Materials</span>
        <span className="font-extrabold text-gray-800">{fmtFull(total)}</span>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ text }) => (
  <p className="text-xs text-slate-400 text-center py-5">{text}</p>
);

// ── Skeleton loader ───────────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      <div className="h-8 bg-gray-100 rounded-lg w-56" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-56 bg-gray-100 rounded-xl" />
        <div className="h-56 bg-gray-100 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => <div key={i} className="h-48 bg-gray-100 rounded-xl" />)}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { hasPermission, user, isSiteRestricted: isStorekeeper } = useAuth();

  const [months, setMonths]           = useState(6);
  // Custom date range — mutually exclusive with the months presets above.
  // Empty strings = preset mode active (unchanged default behavior).
  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');
  const [siteFilter, setSiteFilter]   = useState('');
  const [sites, setSites]             = useState([]);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [popup, setPopup] = useState(null); // { title, items, totalQty, accent, link, linkText }
  const [pendingReqs, setPendingReqs] = useState([]);
  const [pendingTrans, setPendingTrans] = useState([]);

  // Site list for filter dropdown
  useEffect(() => {
    if (!isStorekeeper)
      siteApi.list().then(r => setSites(r.data.data || [])).catch(() => {});
  }, [isStorekeeper]);

  // Analytics load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = (fromDate && toDate) ? { fromDate, toDate } : { months };
        if (!isStorekeeper && siteFilter) params.siteId = siteFilter;
        const [statsRes, reqRes, transRes] = await Promise.allSettled([
          hasPermission('dashboard_view') ? dashboardApi.stats(params) : Promise.resolve(null),
          hasPermission('inventory_view') ? requirementApi.list({ status: 'open' }) : Promise.resolve(null),
          hasPermission('inventory_view') ? transferApi.list({ status: 'pending' }) : Promise.resolve(null),
        ]);
        if (statsRes.value) {
          if (statsRes.status === 'fulfilled') setData(statsRes.value.data.data);
          else setError('Failed to load analytics');
        }
        setPendingReqs(reqRes.value?.data?.data?.slice(0, 5)   || []);
        setPendingTrans(transRes.value?.data?.data?.slice(0, 5) || []);
      } catch {
        setError('Failed to load dashboard data');
      }
      setLoading(false);
    };
    load();
  }, [months, fromDate, toDate, siteFilter]);

  if (loading && !data && hasPermission('dashboard_view')) return <DashboardSkeleton />;

  const kpi          = data?.kpi          || {};
  const trends       = data?.monthlyTrends || [];
  const sites_       = data?.siteStats     || [];
  const dedAn        = data?.deductionAnalytics || {};
  const invBk        = data?.inventoryBreakdown || {};
  const issAn        = data?.issueAnalytics || {};
  const damagedItems           = data?.damagedItems           || [];
  const periodDamagedItems     = data?.periodDamagedItems     || [];
  const lowStockItems          = data?.lowStockItems          || [];
  const underRepairItems       = data?.underRepairItems       || [];
  const matStockBreakdown      = data?.matStockBreakdown      || [];
  const statStockBreakdown     = data?.statStockBreakdown     || [];
  const itStockBreakdown       = data?.itStockBreakdown       || [];
  const activeIssuedBreakdown  = data?.activeIssuedBreakdown  || [];
  const periodIssueBreakdown   = data?.periodIssueBreakdown   || [];
  const cycleDeductionBreakdown = data?.cycleDeductionBreakdown || [];

  // Trend indicators: compare last two months
  const lastT = trends[trends.length - 1] || {};
  const prevT = trends[trends.length - 2] || {};
  const issueTrend  = trendPct(lastT.totalValue, prevT.totalValue);
  const dedTrend    = trendPct(lastT.deductionValue, prevT.deductionValue);

  const maxMatVal  = Math.max(...(issAn.topMatByValue  || []).map(m => m.value), 1);
  const maxStatVal = Math.max(...(issAn.topStatByValue || []).map(s => s.value), 1);
  const maxItVal   = Math.max(...(issAn.topItByValue   || []).map(i => i.value), 1);
  const canViewIT  = hasPermission('it_view');
  const maxSiteVal = Math.max(...sites_.map(s => s.totalInventoryValue), 1);
  const maxSiteDed = Math.max(...(dedAn.bySite || []).map(s => s.amount), 1);

  const hasCustomRange = !!(fromDate && toDate);
  const periodLabel = hasCustomRange
    ? `${new Date(fromDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${new Date(toDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : months === 12 ? '12-month' : months === 3 ? 'Quarterly' : months === 6 ? '6-month' : 'Monthly';

  return (
    <div className="space-y-5">

      {/* ── Generic Item Popup ───────────────────────────────────────────────── */}
      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPopup(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-800">{popup.title}</h3>
              <button onClick={() => setPopup(null)} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
            </div>
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {popup.items.map((item, i) => (
                <li key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 ${popup.rowBg}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{item.name}</p>
                    {item.sub && <p className="text-xs text-slate-400 truncate">{item.sub}</p>}
                  </div>
                  <span className={`text-sm font-bold ml-3 flex-shrink-0 ${popup.accent}`}>{item.qty} unit{item.qty !== 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400">Total: {popup.totalQty} units</span>
              {popup.link && <Link to={popup.link} onClick={() => setPopup(null)} className={`text-xs font-semibold hover:underline ${popup.accent}`}>{popup.linkText || 'View →'}</Link>}
            </div>
          </div>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Hi, {user?.fullName || user?.username} 👋</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isStorekeeper
              ? 'Site inventory and financial intelligence'
              : 'Organisation-wide material value analytics'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Site filter — applies to both current and time-based sections */}
          {!isStorekeeper && sites.length > 0 && (
            <div className="w-44">
              <SearchableSelect
                className="input py-1.5 text-xs"
                options={[{ id: '', label: 'All Sites' }, ...sites.map(s => ({ id: s.id, label: s.name }))]}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder="All Sites"
              />
            </div>
          )}
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-blue-400">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            {new Date().toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</p>}

      {/* ── Current Inventory Overview — always live, ignores every time filter ── */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Current Inventory Overview</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard
            label="Total Inventory Value"
            value={fmtV(kpi.totalInventoryValue)}
            sub={fmtFull(kpi.totalInventoryValue)}
            bg="bg-slate-100" textColor="text-slate-700"
            onClick={() => setPopup({
              title: 'Total Inventory — Materials',
              items: matStockBreakdown.map(i => ({ name: i.name, qty: i.qty })),
              totalQty: matStockBreakdown.reduce((s,i) => s + i.qty, 0),
              rowBg: 'bg-slate-50', accent: 'text-slate-700',
            })}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"/><path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd"/></svg>}
          />
          <KpiCard
            label="Material Stock Value"
            value={fmtV(kpi.matInventoryValue)}
            sub="Safety materials"
            bg="bg-indigo-50" textColor="text-indigo-700"
            onClick={() => setPopup({
              title: 'Material Stock Breakdown',
              items: matStockBreakdown.map(i => ({ name: i.name, qty: i.qty })),
              totalQty: matStockBreakdown.reduce((s,i) => s + i.qty, 0),
              rowBg: 'bg-indigo-50', accent: 'text-indigo-700',
              link: '/inventory', linkText: 'View Inventory →',
            })}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"/></svg>}
          />
          <KpiCard
            label="Stationery Asset Value"
            value={fmtV(kpi.statInventoryValue)}
            sub={`${fmtV(kpi.durableAssetValue)} durable · ${fmtV(kpi.consumableStockValue)} consumable`}
            bg="bg-violet-50" textColor="text-violet-700"
            onClick={() => setPopup({
              title: 'Stationery Stock Breakdown',
              items: statStockBreakdown.map(i => ({ name: i.name, sub: i.sub, qty: i.qty })),
              totalQty: statStockBreakdown.reduce((s,i) => s + i.qty, 0),
              rowBg: 'bg-violet-50', accent: 'text-violet-700',
              link: '/inventory', linkText: 'View Inventory →',
            })}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"/><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd"/></svg>}
          />
          {/* IT is isolated behind it_view same as everywhere else in the IT
              section — a dashboard_view holder without it_view never sees
              this tile (backend already zeroes the underlying figures too). */}
          {canViewIT && (
            <KpiCard
              label="IT Asset Value"
              value={fmtV(kpi.itInventoryValue)}
              sub={`${fmtV(kpi.itDurableAssetValue)} durable · ${fmtV(kpi.itConsumableStockValue)} consumable`}
              bg="bg-cyan-50" textColor="text-cyan-700"
              onClick={() => setPopup({
                title: 'IT Stock Breakdown',
                items: itStockBreakdown.map(i => ({ name: i.name, sub: i.sub, qty: i.qty })),
                totalQty: itStockBreakdown.reduce((s,i) => s + i.qty, 0),
                rowBg: 'bg-cyan-50', accent: 'text-cyan-700',
                link: '/inventory', linkText: 'View Inventory →',
              })}
              icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2h-4l1 2h1a1 1 0 110 2H6a1 1 0 110-2h1l1-2H4a2 2 0 01-2-2V5zm2 0h12v7H4V5z" clipRule="evenodd"/></svg>}
            />
          )}
          <KpiCard
            label="Active Issued Value"
            value={fmtV(kpi.activeTotalIssuedValue)}
            sub={canViewIT
              ? `${fmtV(kpi.activeMatIssuedValue)} mat · ${fmtV(kpi.activeStatIssuedValue)} stat · ${fmtV(kpi.activeItIssuedValue)} IT`
              : `${fmtV(kpi.activeMatIssuedValue)} mat · ${fmtV(kpi.activeStatIssuedValue)} stat`}
            bg="bg-amber-50" textColor="text-amber-700"
            onClick={() => setPopup({
              title: 'Active Issued Items',
              items: activeIssuedBreakdown.map(i => ({ name: i.name, qty: i.qty })),
              totalQty: activeIssuedBreakdown.reduce((s,i) => s + i.qty, 0),
              rowBg: 'bg-amber-50', accent: 'text-amber-700',
            })}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>}
          />
          <KpiCard
            label="Cycle Deduction Total"
            value={fmtV(kpi.cycleDeductionTotal)}
            sub="Current salary cycle"
            bg="bg-red-50" textColor="text-red-600"
            onClick={() => setPopup({
              title: 'Cycle Deductions by Type',
              items: cycleDeductionBreakdown.map(i => ({ name: i.name, qty: i.qty })),
              totalQty: cycleDeductionBreakdown.reduce((s,i) => s + i.qty, 0),
              rowBg: 'bg-red-50', accent: 'text-red-600',
              link: '/salary-deductions', linkText: 'View Deductions →',
            })}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>}
          />
          <KpiCard
            label="Under Repair"
            value={String(kpi.underRepairItems || 0)}
            sub={(() => { const u = underRepairItems.reduce((s,i) => s + i.quantity, 0); return u > 0 ? `${u} unit${u !== 1 ? 's' : ''} pending repair` : 'No items under repair'; })()}
            bg="bg-orange-50" textColor="text-orange-600"
            onClick={underRepairItems.length ? () => setPopup({
              title:    'Items Under Repair',
              items:    underRepairItems.map(i => ({ name: i.itemName, sub: i.siteName, qty: i.quantity })),
              totalQty: underRepairItems.reduce((s,i) => s + i.quantity, 0),
              rowBg:    'bg-orange-50', accent: 'text-orange-600',
              link: '/site-history', linkText: 'View in Site History →',
            }) : undefined}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/></svg>}
          />
        </div>
      </div>

      {/* ── Item Lookup — pick one item, see it across every site at once ───── */}
      <ItemLookupPanel />

      {/* ── Time-Based Analytics — everything below responds to the filter ──── */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Time-Based Analytics</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Preset time range pills */}
            <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-lg px-1 py-1 shadow-sm">
              {[{ v: 1, l: '1M' }, { v: 3, l: '3M' }, { v: 6, l: '6M' }, { v: 12, l: '1Y' }].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => { setMonths(v); setFromDate(''); setToDate(''); }}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-all ${
                    !hasCustomRange && months === v ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            {/* Custom date range */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={e => setFromDate(e.target.value)}
                className="text-xs border-0 focus:ring-0 p-0.5 w-[110px]"
              />
              <span className="text-slate-300 text-xs">–</span>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setToDate(e.target.value)}
                className="text-xs border-0 focus:ring-0 p-0.5 w-[110px]"
              />
              {hasCustomRange && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  className="text-slate-400 hover:text-slate-600 text-xs leading-none ml-1"
                  title="Clear custom range"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <KpiCard
            label={`${periodLabel} Issue Spend`}
            value={fmtV(kpi.periodIssuedValue)}
            sub={`${fmtV(kpi.periodMatValue)} mat · ${fmtV(kpi.periodStatValue)} stat`}
            bg="bg-emerald-50" textColor="text-emerald-700"
            onClick={() => setPopup({
              title: `${periodLabel} Issue Breakdown`,
              items: periodIssueBreakdown.map(i => ({ name: i.name, qty: i.qty })),
              totalQty: periodIssueBreakdown.reduce((s,i) => s + i.qty, 0),
              rowBg: 'bg-emerald-50', accent: 'text-emerald-700',
              link: '/site-history', linkText: 'View Site History →',
            })}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>}
          />
          <KpiCard
            label={`${periodLabel} Purchase Value`}
            value={fmtV(kpi.periodPurchaseValue)}
            sub={`${fmtV(kpi.periodPurchaseValueMat)} mat · ${fmtV(kpi.periodPurchaseValueStat)} stat`}
            bg="bg-sky-50" textColor="text-sky-700"
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M10.75 10.818v2.614A3.13 3.13 0 0011.888 13c.482-.315.612-.648.612-.875 0-.227-.13-.56-.612-.875a3.13 3.13 0 00-1.138-.432zM8.33 8.62c.053.055.115.11.184.164.208.16.46.284.736.363V6.603a2.45 2.45 0 00-.35.13c-.14.065-.27.143-.386.233-.377.292-.514.627-.514.909 0 .184.058.39.202.591.055.078.12.153.128.163z" /><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v2.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V16a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 14.766 14 13.991 14 13c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 10.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/></svg>}
          />
          <KpiCard
            label={`${periodLabel} Damage Value`}
            value={fmtV(kpi.periodDamagedValue)}
            sub={(() => { const u = periodDamagedItems.reduce((s,i) => s + i.qty, 0); return u > 0 ? `${u} unit${u !== 1 ? 's' : ''} damaged` : 'No damage recorded'; })()}
            bg="bg-rose-50" textColor="text-rose-700"
            onClick={periodDamagedItems.length ? () => setPopup({
              title:    `Damaged Items — ${periodLabel}`,
              items:    periodDamagedItems.map(i => ({ name: i.kitName, qty: i.qty })),
              totalQty: periodDamagedItems.reduce((s,i) => s + i.qty, 0),
              rowBg:    'bg-rose-50', accent: 'text-rose-600',
              link: '/site-history', linkText: 'View in Site History →',
            }) : undefined}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>}
          />
        </div>
      </div>

      {/* ── Row 2: Issue Value Trend ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4">

        {/* Monthly Issue Value Trend Chart */}
        <div className="card">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Issue Value Trend</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                ₹ value of materials and stationery issued per month — last {periodLabel.toLowerCase()}
              </p>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-shrink-0">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2 rounded-sm bg-indigo-500 inline-block" />Materials</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2 rounded-sm bg-violet-400 inline-block" />Stationery</span>
            </div>
          </div>

          {trends.length === 0 || trends.every(t => t.totalValue === 0) ? (
            <div className="h-40 flex items-center justify-center">
              <p className="text-xs text-gray-400">No issue value data for this period</p>
            </div>
          ) : (
            <>
              {/* Y-axis hint */}
              <div className="flex justify-between text-[9px] text-gray-300 mb-0.5 px-0.5">
                <span>{fmtV(Math.max(...trends.map(t => t.totalValue)))}</span>
                <span>0</span>
              </div>
              <StackedBarChart data={trends} />

            </>
          )}
        </div>

      </div>

      {/* ── Row 3: Inventory Breakdown + Site Comparison ───────────────────── */}
      <div className={`grid grid-cols-1 gap-4 ${!isStorekeeper && sites_.length > 1 ? 'lg:grid-cols-3' : ''}`}>

        {/* Inventory Value Breakdown */}
        <div className="card">
          <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Inventory Value Breakdown</h3>
          <p className="text-xs text-slate-400 mb-4">Total stock value by asset category</p>
          <InvBreakdown
            mat={invBk.materials || 0}
            durable={invBk.durableAssets || 0}
            consumable={invBk.consumableStock || 0}
          />
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-center">
            <div className="bg-amber-50 rounded-lg p-2">
              <p className="text-[10px] text-amber-500 font-medium">Active Issued</p>
              <p className="text-sm font-extrabold text-amber-700">{fmtV(kpi.activeTotalIssuedValue)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-[10px] text-slate-500 font-medium">Workers</p>
              <p className="text-sm font-extrabold text-slate-700">{kpi.activeWorkers}/{kpi.totalWorkers}</p>
            </div>
          </div>
        </div>

        {/* Site Comparison (only when multi-site) */}
        {!isStorekeeper && sites_.length > 1 && (
          <div className="lg:col-span-2 card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Site Inventory Value Comparison</h3>
                <p className="text-xs text-slate-400 mt-0.5">Stock value, active issued value and cycle deductions per site</p>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 flex-shrink-0 flex-wrap justify-end">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-indigo-500 rounded-sm" />Mat</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-violet-400 rounded-sm" />Stat</span>
                {canViewIT && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-cyan-500 rounded-sm" />IT</span>}
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-sm" />Issued</span>
                {sites_.some(s => s.cycleDeductionTotal > 0) && (
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-sm" />Ded</span>
                )}
              </div>
            </div>
            <div className="space-y-4">
              {sites_.map(s => {
                const grandMax = Math.max(maxSiteVal, s.activeIssuedValue, 1);
                const matPct   = (s.matInventoryValue  / grandMax) * 100;
                const statPct  = (s.statInventoryValue / grandMax) * 100;
                const itPct    = (s.itInventoryValue   / grandMax) * 100;
                const issPct   = (s.activeIssuedValue  / grandMax) * 100;
                const dedPct   = (s.cycleDeductionTotal / grandMax) * 100;
                return (
                  <div key={s.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-800">{s.name}</span>
                      <div className="text-right text-[10px] text-gray-500">
                        <span className="font-semibold text-gray-700">{fmtV(s.totalInventoryValue)}</span>
                        {s.activeIssuedValue > 0 && <span className="ml-2 text-amber-600">· {fmtV(s.activeIssuedValue)} issued</span>}
                        {s.cycleDeductionTotal > 0 && <span className="ml-2 text-red-500">· {fmtV(s.cycleDeductionTotal)} ded</span>}
                      </div>
                    </div>
                    {/* Inventory stacked bar */}
                    <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 gap-px mb-0.5">
                      {matPct  > 0 && <div className="bg-indigo-500 h-3" style={{ width: `${matPct}%` }}  title={`Mat: ${fmtV(s.matInventoryValue)}`} />}
                      {statPct > 0 && <div className="bg-violet-400 h-3" style={{ width: `${statPct}%` }} title={`Stat: ${fmtV(s.statInventoryValue)}`} />}
                      {canViewIT && itPct > 0 && <div className="bg-cyan-500 h-3" style={{ width: `${itPct}%` }} title={`IT: ${fmtV(s.itInventoryValue)}`} />}
                    </div>
                    {/* Issued + deductions bar */}
                    <div className="flex h-1.5 rounded-full overflow-hidden bg-gray-50 gap-px">
                      {issPct > 0 && <div className="bg-amber-400 h-1.5" style={{ width: `${issPct}%` }} title={`Issued: ${fmtV(s.activeIssuedValue)}`} />}
                      {dedPct > 0 && <div className="bg-red-400  h-1.5" style={{ width: `${dedPct}%` }}  title={`Deductions: ${fmtV(s.cycleDeductionTotal)}`} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Row 3.5: Materials by Type ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4">
        <div className="card">
          <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Materials by Type</h3>
          <p className="text-xs text-slate-400 mb-4">Stock value and currently issued value per material type</p>
          <MatTypeBreakdown byType={invBk.materialsByType} />
        </div>
      </div>

      {/* ── Row 4: Top Items by Issued Value ───────────────────────────────── */}
      {((issAn.topMatByValue || []).length > 0 || (issAn.topStatByValue || []).length > 0 || (canViewIT && (issAn.topItByValue || []).length > 0)) && (
        <div className={`grid grid-cols-1 gap-4 ${canViewIT ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {(issAn.topMatByValue || []).length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Top Materials by Issue Value</h3>
              <p className="text-xs text-slate-400 mb-4">
                Highest-value materials issued — {periodLabel.toLowerCase()}
              </p>
              <div className="space-y-3">
                {(issAn.topMatByValue || []).map((m, i) => (
                  <HValBar
                    key={i}
                    label={m.name}
                    value={m.value}
                    max={maxMatVal}
                    color="bg-indigo-500"
                    sub={`${m.count} issuance${m.count !== 1 ? 's' : ''} · avg ${fmtV(m.value / m.count)} each`}
                  />
                ))}
              </div>
            </div>
          )}
          {(issAn.topStatByValue || []).length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Top Stationery by Issue Value</h3>
              <p className="text-xs text-slate-400 mb-4">
                Highest-value stationery issued — {periodLabel.toLowerCase()}
              </p>
              <div className="space-y-3">
                {(issAn.topStatByValue || []).map((s, i) => (
                  <HValBar
                    key={i}
                    label={s.name}
                    value={s.value}
                    max={maxStatVal}
                    color="bg-violet-500"
                    sub={`${s.count} issuance${s.count !== 1 ? 's' : ''} · avg ${fmtV(s.value / s.count)} each`}
                  />
                ))}
              </div>
            </div>
          )}
          {canViewIT && (issAn.topItByValue || []).length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Top IT by Issue Value</h3>
              <p className="text-xs text-slate-400 mb-4">
                Highest-value IT items issued — {periodLabel.toLowerCase()}
              </p>
              <div className="space-y-3">
                {(issAn.topItByValue || []).map((i, idx) => (
                  <HValBar
                    key={idx}
                    label={i.name}
                    value={i.value}
                    max={maxItVal}
                    color="bg-cyan-500"
                    sub={`${i.count} issuance${i.count !== 1 ? 's' : ''} · avg ${fmtV(i.value / i.count)} each`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Row 5: Low Stock Alert ──────────────────────────────────────────── */}
      {kpi.lowStockMaterials > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button type="button" onClick={() => setPopup({
            title:    `Low Stock Materials (≤5 units)`,
            items:    lowStockItems.map(i => ({ name: i.kitName, sub: i.siteName, qty: i.quantity })),
            totalQty: lowStockItems.reduce((s,i) => s + i.quantity, 0),
            rowBg:    'bg-amber-50', accent: 'text-amber-700',
            link: '/inventory', linkText: 'View Inventory →',
          })} className="card group border-amber-200 bg-amber-50 w-full text-left cursor-pointer hover:shadow-md hover:border-amber-300 transition-all relative">
            <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-[9px] font-bold text-amber-500 bg-amber-100 rounded px-1 py-0.5 leading-none select-none">details</span>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 flex-shrink-0">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">{kpi.lowStockMaterials} Low Stock Line{kpi.lowStockMaterials !== 1 ? 's' : ''}</p>
                <p className="text-xs text-amber-600">{lowStockItems.reduce((s,i) => s + i.quantity, 0)} units remaining — replenishment needed</p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* ── Row 6: Operational Panels ──────────────────────────────────────── */}
      {(pendingReqs.length > 0 || pendingTrans.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Open Requirements</h3>
              <Link to="/requirements" className="text-xs text-blue-600 font-medium">View all →</Link>
            </div>
            {pendingReqs.length === 0 ? <EmptyState text="No open requirements" /> : (
              <div className="space-y-0.5">
                {pendingReqs.map(r => (
                  <Link key={r.id} to={`/requirements/${r.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{r.kit?.name}</p>
                      <p className="text-xs text-amber-600 font-medium">{r.quantityRequired - r.quantityFulfilled} units needed</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Pending Transfers</h3>
              <Link to="/transfers" className="text-xs text-blue-600 font-medium">View all →</Link>
            </div>
            {pendingTrans.length === 0 ? <EmptyState text="No pending transfers" /> : (
              <div className="space-y-0.5">
                {pendingTrans.map(t => (
                  <Link key={t.id} to={`/transfers/${t.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{t.kit?.name}</p>
                      <p className="text-xs text-gray-400">
                        {t.fromSite?.name} → {t.toSite?.name}
                        <span className="ml-1.5 text-blue-600 font-medium">{t.quantity} units</span>
                      </p>
                    </div>
                    <StatusBadge status="pending" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
